import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { DatePickerFr } from '../ui/DatePickerFr';
import { Label } from '../ui/label';

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

import { UserCheck, Users, Building2, Clock, RefreshCw, Plus, CheckCircle, Filter, X, Calendar, MapPin, FileSpreadsheet, FileDown } from 'lucide-react';
import OscopLogo from '../brand/OscopLogo';

export default function AdhesionsHeader({ ctx }) {
  const { t, stats, adhesions, filters, setFilters, showFilters, setShowFilters, exporting, setOpenCreateAdhesion, filteredAdhesions, resetFilters, exportCSV, exportPDF, TERRITOIRES } = ctx;
  return (
    <>
      {/* En-tête premium O'SCOP — Navy + Or */}
      <div className="relative overflow-hidden rounded-2xl shadow-[0_20px_60px_-15px_rgba(10,61,98,0.45)]" data-testid="adhesions-header-premium">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a3d62] via-[#0c4a78] to-[#08304d]" />
        <div
          className="absolute inset-0 opacity-[0.08] mix-blend-overlay"
          style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='80' viewBox='0 0 80 80'%3E%3Cg fill='%23DEA900' fill-opacity='0.5'%3E%3Ccircle cx='1' cy='1' r='1'/%3E%3C/g%3E%3C/svg%3E\")" }}
        />
        <div className="absolute -right-20 -top-20 w-72 h-72 rounded-full bg-[#DEA900]/15 blur-3xl" />
        <div className="relative p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-[#DEA900] to-[#b88600] shadow-lg shadow-[#DEA900]/30 flex items-center justify-center ring-1 ring-[#f5cc3e]/40">
                <OscopLogo size={32} variant="on-navy" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-bold tracking-[0.18em] text-[#f5cc3e] uppercase">O'SCOP Outremer</span>
                  <span className="h-px w-8 bg-[#f5cc3e]/40" />
                  <span className="text-[10px] tracking-wider text-white/60 uppercase">Adhésions & souscriptions</span>
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-white tracking-tight mt-0.5">
                  {t('adhesions.management_title', 'Gestion des Adhésions')}
                </h2>
                <p className="text-sm text-white/70 mt-0.5">
                  {t('adhesions.management_desc', 'Créer, gérer et suivre toutes les adhésions')}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="outline"
                className="bg-white/10 hover:bg-white/20 border-white/25 text-white"
                onClick={() => setShowFilters(!showFilters)}
                data-testid="adhesions-filters-btn"
              >
                <Filter className="h-4 w-4 mr-1.5" />
                Filtres
                {Object.values(filters).some(v => v && v !== 'all') && (
                  <Badge className="ml-2 bg-[#DEA900] hover:bg-[#DEA900] text-[#0a3d62] font-semibold">{Object.values(filters).filter(v => v && v !== 'all').length}</Badge>
                )}
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="bg-white/10 hover:bg-white/20 border-white/25 text-white"
                onClick={exportCSV}
                disabled={exporting || filteredAdhesions.length === 0}
                data-testid="adhesions-csv-btn"
              >
                <FileSpreadsheet className="h-4 w-4 mr-1.5" />
                Export CSV
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="bg-white/10 hover:bg-white/20 border-white/25 text-white"
                onClick={exportPDF}
                disabled={exporting || filteredAdhesions.length === 0}
                data-testid="adhesions-pdf-btn"
              >
                <FileDown className="h-4 w-4 mr-1.5" />
                Export PDF
              </Button>
              <Button
                size="sm"
                className="bg-[#DEA900] hover:bg-[#f5cc3e] text-[#0a3d62] font-semibold border-0"
                onClick={() => setOpenCreateAdhesion(true)}
                data-testid="adhesions-create-btn"
              >
                <Plus className="h-4 w-4 mr-1.5" />
                {t('adhesions.create', 'Créer une adhésion')}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Panneau de filtres avancés */}
      {showFilters && (
        <Card className="border border-[#0a3d62]/15 bg-gradient-to-br from-[#0a3d62]/3 to-[#DEA900]/3">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2 text-[#0a3d62]">
                <Filter className="h-5 w-5 text-[#DEA900]" />
                Filtres avancés
              </CardTitle>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={resetFilters} className="text-[#0a3d62] hover:bg-[#0a3d62]/5">
                  <X className="h-4 w-4 mr-1" />
                  Réinitialiser
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setShowFilters(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
              {/* Recherche */}
              <div className="lg:col-span-2">
                <Label className="text-sm text-gray-600">Recherche</Label>
                <Input
                  placeholder="Nom, email ou numéro..."
                  value={filters.recherche}
                  onChange={(e) => setFilters(prev => ({ ...prev, recherche: e.target.value }))}
                  className="mt-1"
                />
              </div>
              
              {/* Source */}
              <div>
                <Label className="text-sm text-gray-600">Source</Label>
                <Select value={filters.source} onValueChange={(v) => setFilters(prev => ({ ...prev, source: v }))}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Toutes" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes les sources</SelectItem>
                    <SelectItem value="ftpe">FTPE Outremer</SelectItem>
                    <SelectItem value="rcd">RCD Solidaire</SelectItem>
                    <SelectItem value="dos_domium">DOS DOMIUM</SelectItem>
                    <SelectItem value="adhesion">Adhésion Générale</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Type de membre */}
              <div>
                <Label className="text-sm text-gray-600">Type d&apos;adhérent</Label>
                <Select value={filters.membreType} onValueChange={(v) => setFilters(prev => ({ ...prev, membreType: v }))}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Tous" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous</SelectItem>
                    <SelectItem value="societe">Sociétés</SelectItem>
                    <SelectItem value="particulier">Particuliers</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Territoire */}
              <div>
                <Label className="text-sm text-gray-600">Territoire</Label>
                <Select value={filters.territoire} onValueChange={(v) => setFilters(prev => ({ ...prev, territoire: v }))}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Tous" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les territoires</SelectItem>
                    {TERRITOIRES.map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Statut */}
              <div>
                <Label className="text-sm text-gray-600">Statut</Label>
                <Select value={filters.statut} onValueChange={(v) => setFilters(prev => ({ ...prev, statut: v }))}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Tous" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les statuts</SelectItem>
                    <SelectItem value="active">
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-emerald-500" />
                        Actives / Validées
                      </span>
                    </SelectItem>
                    <SelectItem value="pending">
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-amber-500" />
                        En attente
                      </span>
                    </SelectItem>
                    <SelectItem value="expired">
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-gray-500" />
                        Expirées / Annulées
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Période */}
              <div className="lg:col-span-2 grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-sm text-gray-600">Date début</Label>
                  <DatePickerFr
                    value={filters.dateDebut}
                    onChange={(v) => setFilters(prev => ({ ...prev, dateDebut: v }))}
                    className="mt-1 w-full"
                  />
                </div>
                <div>
                  <Label className="text-sm text-gray-600">Date fin</Label>
                  <DatePickerFr
                    value={filters.dateFin}
                    onChange={(v) => setFilters(prev => ({ ...prev, dateFin: v }))}
                    className="mt-1 w-full"
                  />
                </div>
              </div>
            </div>
            
            {/* Résumé des filtres actifs */}
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <span className="text-sm text-gray-600">Résultats :</span>
              <Badge className="bg-indigo-600 text-white font-semibold">{filteredAdhesions.length} adhésion(s)</Badge>
              {filters.source !== 'all' && (
                <Badge variant="outline" className="gap-1">
                  Source: {filters.source}
                  <X className="h-3 w-3 cursor-pointer" onClick={() => setFilters(prev => ({ ...prev, source: 'all' }))} />
                </Badge>
              )}
              {filters.territoire !== 'all' && (
                <Badge variant="outline" className="gap-1">
                  <MapPin className="h-3 w-3" />
                  {filters.territoire}
                  <X className="h-3 w-3 cursor-pointer" onClick={() => setFilters(prev => ({ ...prev, territoire: 'all' }))} />
                </Badge>
              )}
              {filters.dateDebut && (
                <Badge variant="outline" className="gap-1">
                  <Calendar className="h-3 w-3" />
                  Depuis {filters.dateDebut}
                  <X className="h-3 w-3 cursor-pointer" onClick={() => setFilters(prev => ({ ...prev, dateDebut: '' }))} />
                </Badge>
              )}
              {filters.dateFin && (
                <Badge variant="outline" className="gap-1">
                  <Calendar className="h-3 w-3" />
                  Jusqu'au {filters.dateFin}
                  <X className="h-3 w-3 cursor-pointer" onClick={() => setFilters(prev => ({ ...prev, dateFin: '' }))} />
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Statistiques — Dashboard dynamique ultra pro */}
      {(() => {
        // Calcul des compteurs dynamiques par source et type de membre
        const byCounts = adhesions.reduce((acc, a) => {
          const src = a.source || 'autre';
          const mt = a.membre_type || 'particulier';
          const statut = (a.statut || a.status || '').toLowerCase();
          const isActive = ['active', 'validee', 'completed', 'paid', 'signed', 'signee'].includes(statut);
          acc.totals.global += 1;
          acc.totals.actives += isActive ? 1 : 0;
          acc.totals.societe += mt === 'societe' ? 1 : 0;
          acc.totals.particulier += mt === 'particulier' ? 1 : 0;
          if (!acc.bySource[src]) acc.bySource[src] = { total: 0, actives: 0, montant: 0, societe: 0, particulier: 0 };
          acc.bySource[src].total += 1;
          acc.bySource[src].actives += isActive ? 1 : 0;
          acc.bySource[src].montant += parseFloat(a.montant || 0) || 0;
          acc.bySource[src][mt] = (acc.bySource[src][mt] || 0) + 1;
          return acc;
        }, { totals: { global: 0, actives: 0, societe: 0, particulier: 0 }, bySource: {} });

        const sourceConfig = {
          adhesion: { label: 'Adhésion Générale', icon: '👥', gradient: 'from-[#0a3d62] to-[#0c4a78]', bg: 'from-[#0a3d62]/4 to-[#0a3d62]/8', border: 'border-l-[#0a3d62]' },
          ftpe: { label: 'FTPE Outremer', icon: '🛡️', gradient: 'from-[#DEA900] to-[#b88600]', bg: 'from-[#DEA900]/6 to-[#DEA900]/12', border: 'border-l-[#DEA900]' },
          rcd: { label: 'RCD Solidaire', icon: '🏗️', gradient: 'from-[#0c4a78] to-[#08304d]', bg: 'from-[#0a3d62]/5 to-slate-50', border: 'border-l-[#0c4a78]' },
          dos_domium: { label: 'DOS DOMIUM', icon: '🏠', gradient: 'from-emerald-700 to-emerald-900', bg: 'from-emerald-50/40 to-white', border: 'border-l-emerald-600' },
        };
        const totalEnAttente = (stats?.en_attente || 0) + (stats?.ftpe?.en_attente || 0) + (stats?.rcd?.en_cours || 0);

        return (
          <div className="space-y-4">
            {/* KPI globaux — premium O'SCOP */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="group relative bg-white border border-slate-200/80 rounded-2xl p-4 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_12px_40px_-12px_rgba(10,61,98,0.18)]">
                <span className="absolute left-0 top-4 bottom-4 w-1 rounded-r-full bg-[#0a3d62]" />
                <div className="flex items-center justify-between mb-2 pl-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-bold text-[#0a3d62] uppercase tracking-wider">Total Global</p>
                    <p className="text-3xl font-bold text-[#0a3d62] mt-1 leading-none tabular-nums">{byCounts.totals.global}</p>
                  </div>
                  <div className="w-11 h-11 rounded-xl bg-[#0a3d62]/8 flex items-center justify-center flex-shrink-0">
                    <Users className="h-5 w-5 text-[#0a3d62]" strokeWidth={2.2} />
                  </div>
                </div>
                <div className="pt-2 border-t border-slate-100 flex items-center justify-between text-[10px] pl-2">
                  <span className="text-[#0a3d62] font-semibold">{byCounts.totals.societe} sociétés</span>
                  <span className="text-slate-500">{byCounts.totals.particulier} particuliers</span>
                </div>
              </div>

              <div className="group relative bg-white border border-slate-200/80 rounded-2xl p-4 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_12px_40px_-12px_rgba(16,185,129,0.18)]">
                <span className="absolute left-0 top-4 bottom-4 w-1 rounded-r-full bg-emerald-500" />
                <div className="flex items-center justify-between mb-2 pl-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-bold text-emerald-700 uppercase tracking-wider">Actives / Validées</p>
                    <p className="text-3xl font-bold text-emerald-700 mt-1 leading-none tabular-nums">{byCounts.totals.actives}</p>
                  </div>
                  <div className="w-11 h-11 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="h-5 w-5 text-emerald-600" strokeWidth={2.2} />
                  </div>
                </div>
                <div className="pt-2 border-t border-slate-100 pl-2">
                  <div className="h-1.5 rounded-full bg-emerald-100 overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-emerald-500 to-emerald-600 transition-all duration-700 rounded-full"
                      style={{ width: `${byCounts.totals.global ? Math.round((byCounts.totals.actives / byCounts.totals.global) * 100) : 0}%` }}
                    />
                  </div>
                  <span className="text-[10px] text-emerald-600 font-semibold mt-1 inline-block">
                    {byCounts.totals.global ? Math.round((byCounts.totals.actives / byCounts.totals.global) * 100) : 0}% taux de conversion
                  </span>
                </div>
              </div>

              <div className="group relative bg-white border border-slate-200/80 rounded-2xl p-4 transition-all duration-300 hover:-translate-y-0.5 ring-1 ring-[#DEA900]/30 shadow-[0_8px_24px_-12px_rgba(222,169,0,0.20)]">
                <span className="absolute left-0 top-4 bottom-4 w-1 rounded-r-full bg-[#DEA900]" />
                <div className="flex items-center justify-between mb-2 pl-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-bold text-[#b88600] uppercase tracking-wider">En Attente</p>
                    <p className="text-3xl font-bold text-[#b88600] mt-1 leading-none tabular-nums">{totalEnAttente}</p>
                  </div>
                  <div className="w-11 h-11 rounded-xl bg-[#DEA900]/12 flex items-center justify-center flex-shrink-0">
                    <Clock className="h-5 w-5 text-[#b88600]" strokeWidth={2.2} />
                  </div>
                </div>
                <div className="pt-2 border-t border-slate-100 pl-2">
                  <span className="text-[10px] text-[#b88600] font-semibold">À traiter / valider</span>
                </div>
              </div>

              <div className="group relative bg-white border border-slate-200/80 rounded-2xl p-4 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_12px_40px_-12px_rgba(244,63,94,0.20)]">
                <span className="absolute left-0 top-4 bottom-4 w-1 rounded-r-full bg-rose-500" />
                <div className="flex items-center justify-between mb-2 pl-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-bold text-rose-700 uppercase tracking-wider">À Renouveler</p>
                    <p className="text-3xl font-bold text-rose-600 mt-1 leading-none tabular-nums">{stats?.a_renouveler || 0}</p>
                  </div>
                  <div className="w-11 h-11 rounded-xl bg-rose-50 flex items-center justify-center flex-shrink-0">
                    <RefreshCw className="h-5 w-5 text-rose-600" strokeWidth={2.2} />
                  </div>
                </div>
                <div className="pt-2 border-t border-slate-100 pl-2">
                  <span className="text-[10px] text-rose-600 font-semibold">Avant 30 jours</span>
                </div>
              </div>
            </div>

            {/* Cards par SOURCE — cliquables (filtres) premium */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {Object.entries(sourceConfig).map(([key, conf]) => {
                const data = byCounts.bySource[key] || { total: 0, actives: 0, montant: 0, societe: 0, particulier: 0 };
                const isActive = filters.source === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setFilters((p) => ({ ...p, source: isActive ? 'all' : key }))}
                    className={`text-left rounded-2xl border transition-all overflow-hidden ${isActive ? 'border-[#DEA900] shadow-[0_12px_36px_-12px_rgba(222,169,0,0.35)] ring-1 ring-[#DEA900]/30' : 'border-slate-200/80 hover:border-slate-300 hover:shadow-md hover:-translate-y-0.5 duration-300'}`}
                    data-testid={`source-card-${key}`}
                  >
                    <div className={`bg-gradient-to-r ${conf.gradient} text-white px-3 py-2.5 flex items-center justify-between`}>
                      <span className="text-xs font-bold uppercase tracking-wider flex items-center gap-1.5">
                        <span className="text-base">{conf.icon}</span>
                        {conf.label}
                      </span>
                      {isActive && <Badge className="bg-[#DEA900] hover:bg-[#DEA900] text-[#0a3d62] text-[10px] px-1.5 py-0 h-5 border-0 font-semibold">Filtre actif</Badge>}
                    </div>
                    <div className={`bg-gradient-to-br ${conf.bg} p-3 space-y-2`}>
                      <div className="flex items-baseline justify-between">
                        <span className="text-3xl font-bold text-[#0a3d62] leading-none tabular-nums">{data.total}</span>
                        <span className="text-[10px] text-slate-500 font-semibold">adhésion(s)</span>
                      </div>
                      <div className="flex flex-wrap gap-1 text-[10px]">
                        <span className="inline-flex items-center gap-1 bg-white/80 px-1.5 py-0.5 rounded font-semibold text-[#0a3d62]">
                          <Building2 className="h-2.5 w-2.5" /> {data.societe || 0}
                        </span>
                        <span className="inline-flex items-center gap-1 bg-white/80 px-1.5 py-0.5 rounded font-semibold text-[#0a3d62]">
                          <UserCheck className="h-2.5 w-2.5" /> {data.particulier || 0}
                        </span>
                        {data.actives > 0 && (
                          <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-semibold">
                            <CheckCircle className="h-2.5 w-2.5" /> {data.actives}
                          </span>
                        )}
                      </div>
                      {data.montant > 0 && (
                        <p className="text-[10px] text-slate-500 pt-1 border-t border-white/50">
                          <span className="font-semibold text-slate-700">{data.montant.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €</span> de montant
                        </p>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Toggle Société / Particulier ultra pro */}
            <div className="flex items-center gap-2 bg-slate-100 rounded-xl p-1.5 w-fit">
              <button
                type="button"
                onClick={() => setFilters((p) => ({ ...p, membreType: 'all' }))}
                className={`px-4 py-2 text-xs font-semibold rounded-lg transition-all flex items-center gap-2 ${filters.membreType === 'all' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-600 hover:text-slate-900'}`}
                data-testid="toggle-membre-type-all"
              >
                <Users className="h-3.5 w-3.5" />
                Tous
                <span className="text-[10px] font-mono opacity-70">{byCounts.totals.global}</span>
              </button>
              <button
                type="button"
                onClick={() => setFilters((p) => ({ ...p, membreType: 'societe' }))}
                className={`px-4 py-2 text-xs font-semibold rounded-lg transition-all flex items-center gap-2 ${filters.membreType === 'societe' ? 'bg-white shadow-sm text-indigo-700' : 'text-slate-600 hover:text-slate-900'}`}
                data-testid="toggle-membre-type-societe"
              >
                <Building2 className="h-3.5 w-3.5" />
                Sociétés
                <span className="text-[10px] font-mono opacity-70">{byCounts.totals.societe}</span>
              </button>
              <button
                type="button"
                onClick={() => setFilters((p) => ({ ...p, membreType: 'particulier' }))}
                className={`px-4 py-2 text-xs font-semibold rounded-lg transition-all flex items-center gap-2 ${filters.membreType === 'particulier' ? 'bg-white shadow-sm text-emerald-700' : 'text-slate-600 hover:text-slate-900'}`}
                data-testid="toggle-membre-type-particulier"
              >
                <UserCheck className="h-3.5 w-3.5" />
                Particuliers
                <span className="text-[10px] font-mono opacity-70">{byCounts.totals.particulier}</span>
              </button>
            </div>
          </div>
        );
      })()}
    </>
  );
}
